import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import { Box, Row, useMedia } from '@opendoor/bricks-next';
import { useRouter } from 'next/router';

import { getInitials } from 'components/landing-pages-v2/utils/getInitials';
import usePrevious from 'components/landing-pages-v2/utils/usePrevious';

import { useObservability } from '../../../../helpers/observability';
import { COSMOS_URL } from '../../../globals';
import MenuPanel from '../menu/MenuPanel';
import type { MegaNavItem, NavigationProps } from '../types';
import { AccountSection } from './AccountSection';
import { LogoSection } from './LogoSection';
import { MegaNav } from './MegaNav';
import { SkipLink } from './SkipLink';

interface HeaderProps extends NavigationProps {
  megaNavData?: MegaNavItem[];
  closeMenuRef?: MutableRefObject<() => void>;
}

const redirectToLogin = () => {
  document.location.href = `${COSMOS_URL}/login?redirectTo=${encodeURIComponent(
    window.location.pathname,
  )}`;
};

const Header = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaTitle,
  ctaLink,
  megaNavData: megaNavDataProp,
  closeMenuRef,
}: HeaderProps) => {
  const { pathname } = useRouter();
  const { largerThanSM } = useMedia();

  const { trackEvent } = useObservability();
  const { user: human, authentication } = useAuth();

  const isLoggedIn = authentication.state === 'authenticated';

  const initials = getInitials(human?.fullName, human?.firstName);
  const [isVisible, setIsVisible] = useState(false);
  const prevVisible = usePrevious(isVisible);
  const toggleMenuOpen = () => setIsVisible(!isVisible);

  const trackMenuClick = useCallback(() => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'menu', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  const trackAccountClick = useCallback(() => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  // Hang on to burger button ref for focus management
  const ctaRef = useRef<HTMLButtonElement>(null);
  // When menu close animation is complete
  // and it was previously open.
  // This prevents a focus from the initial animation state
  // on page load.
  const exitComplete = () => {
    if (prevVisible === true) {
      const activeEl = document.activeElement;
      const isInputFocused =
        activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement;
      if (!isInputFocused) {
        ctaRef.current?.focus();
      }
    }
  };

  //Hide Sign button on the sign in page
  const showLogin = !(pathname.includes('/login') || pathname.includes('/account'));

  useEffect(() => {
    if (closeMenuRef) {
      closeMenuRef.current = () => setIsVisible(false);
    }
  }, [closeMenuRef]);

  return (
    <>
      {!largerThanSM && (
        <MenuPanel
          slug={slug}
          menuPanelNavLinks={menuPanelNavLinks}
          mainNavLinks={mainNavLinks}
          ctaTitle={ctaTitle}
          ctaLink={ctaLink}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          exitComplete={exitComplete}
          megaNavData={megaNavDataProp}
        />
      )}

      <Box
        display="block"
        backgroundColor="$backgroundPrimary"
        height={72}
        zIndex={2000}
        px="$10x"
        $largerThanSM={{ px: 0 }}
        $platform-web={{ position: 'sticky', top: 0 }}
      >
        <Box
          height={72}
          mx="auto"
          maxWidth={1440}
          $largerThanSM={{ px: '$20x' }}
          $largerThanMD={{ px: '$32x' }}
          $platform-web={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto' }}
        >
          <SkipLink />
          <LogoSection />
          {/* 1 / -1 keeps things centered despite size of left and right columns */}
          <MegaNav slug={slug} mainNavLinks={mainNavLinks} megaNavData={megaNavDataProp} />
          <Row
            alignItems="center"
            gap="$12x"
            pl="$8x"
            $platform-web={{ gridRow: '1 / 2', gridColumn: '-1 / -2' }}
          >
            <AccountSection
              isLoggedIn={isLoggedIn}
              showLogin={showLogin}
              initials={initials}
              redirectToLogin={redirectToLogin}
              trackAccountClick={trackAccountClick}
              trackMenuClick={trackMenuClick}
              onMobileMenuToggle={toggleMenuOpen}
              isMobileMenuVisible={isVisible}
              hamburgerRef={ctaRef}
            />
          </Row>
        </Box>
      </Box>
    </>
  );
};

export default Header;
